import React, { useEffect, useMemo, useState } from "react";
import { Delete as DeleteIcon } from "@mui/icons-material";
import {
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    IconButton,
    Link,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Logger from "../../../services/Logger";
import ConfirmDialog from "../../components/ConfirmDialog";
import KeywordBox from "../../components/KeywordBox";
import PlayRowDetailDialog from "../../pzl_common/PlayRowDetailDialog";
import AdminWordWheelPlayService from "./AdminWordWheelPlayService";
import { getSolveColumns } from "./getSolveColumns";
import WordWheelPlayTimelineChart from "./WordWheelPlayTimelineChart";

export default function AdminWordWheelPlay({ wordWheelTplId }) {
    const [searchParams] = useSearchParams();
    const cloudUserId = (searchParams.get("cloudUserId") || "").trim();
    const sessionId = (searchParams.get("sessionId") || "").trim();
    const playerLabel = (searchParams.get("player") || "").trim();
    const playerFilter = useMemo(
        () => ({ cloudUserId, sessionId: cloudUserId ? "" : sessionId }),
        [cloudUserId, sessionId],
    );

    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 15 });
    const [sort, setSort] = useState([{ field: "createdAt", sort: "desc" }]);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [keyword, setKeyword] = useState("");
    const [completionStatus, setCompletionStatus] = useState({
        all: true,
        completed: false,
        incomplete: false,
    });
    const [onlyStarted, setOnlyStarted] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [, setSelectedRowDataForDelete] = useState([]);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedRowForDetail, setSelectedRowForDetail] = useState(null);
    const [viewMode, setViewMode] = useState("list");
    const [chartRows, setChartRows] = useState([]);
    const [chartLoading, setChartLoading] = useState(false);
    const [chartError, setChartError] = useState("");

    useEffect(() => {
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [playerFilter.cloudUserId, playerFilter.sessionId]);

    useEffect(() => {
        const isCompleted = completionStatus.completed ? true : completionStatus.incomplete ? false : null;
        AdminWordWheelPlayService.requestPuzzlePlay(
            wordWheelTplId,
            keyword,
            pageModel,
            sort,
            setRows,
            setRowCount,
            isCompleted,
            onlyStarted,
            playerFilter.cloudUserId,
            playerFilter.sessionId,
        );
    }, [pageModel, sort, wordWheelTplId, keyword, completionStatus, onlyStarted, playerFilter]);

    useEffect(() => {
        if (viewMode !== "chart" || !(playerFilter.cloudUserId || playerFilter.sessionId)) {
            return undefined;
        }
        let cancelled = false;
        const loadChart = async () => {
            setChartLoading(true);
            setChartError("");
            try {
                const isCompleted = completionStatus.completed ? true : completionStatus.incomplete ? false : null;
                const { rows } = await AdminWordWheelPlayService.fetchPuzzlePlays({
                    wordWheelTplId,
                    keyword,
                    pageModel: { page: 0, pageSize: 1000 },
                    sort: [{ field: "createdAt", sort: "asc" }],
                    isCompleted,
                    onlyStarted,
                    cloudUserId: playerFilter.cloudUserId,
                    sessionId: playerFilter.sessionId,
                });
                if (!cancelled) {
                    setChartRows(rows);
                }
            } catch (error) {
                Logger.error("Error loading word wheel play timeline:", error);
                if (!cancelled) {
                    setChartError("Failed to load timeline.");
                    setChartRows([]);
                }
            } finally {
                if (!cancelled) {
                    setChartLoading(false);
                }
            }
        };
        loadChart();
        return () => {
            cancelled = true;
        };
    }, [viewMode, wordWheelTplId, keyword, completionStatus, onlyStarted, playerFilter]);

    const handleCell = (cell) => {
        if (cell.field === "id" || cell.field === "__check__" || cell.field == null) return;
        const rowData = rows.find((row) => row.id === cell.id);
        if (rowData) {
            setSelectedRowForDetail(rowData);
            setDetailDialogOpen(true);
        }
    };

    const handleRowDoubleClick = (params) => {
        setSelectedRowForDetail(params.row);
        setDetailDialogOpen(true);
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setSelectedRowForDetail(null);
    };

    const handleSort = (model) => {
        setSort(model);
    };

    const handleSelectionModel = (newSelection) => {
        setSelectedRowIds(newSelection);
        setSelectedRowDataForDelete(rows.filter((row) => newSelection.includes(row.id)));
    };

    const handleCompletionStatusChange = (status) => {
        setCompletionStatus((prev) => {
            const newStatus = { ...prev };
            if (status === "all") {
                newStatus.all = true;
                newStatus.completed = false;
                newStatus.incomplete = false;
            } else {
                newStatus.all = false;
                newStatus[status] = !prev[status];
            }
            return newStatus;
        });
    };

    const handleDeleteClick = () => {
        if (selectedRowIds.length > 0) setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async (targetIds) => {
        try {
            await AdminWordWheelPlayService.deletePlays(targetIds);
            const isCompleted = completionStatus.completed ? true : completionStatus.incomplete ? false : null;
            await AdminWordWheelPlayService.requestPuzzlePlay(
                wordWheelTplId,
                keyword,
                pageModel,
                sort,
                setRows,
                setRowCount,
                isCompleted,
                onlyStarted,
                playerFilter.cloudUserId,
                playerFilter.sessionId,
            );
            setSelectedRowIds([]);
            setSelectedRowDataForDelete([]);
        } catch (error) {
            Logger.error("Error deleting word wheel play:", error);
        }
    };

    const hasPlayerFilter = Boolean(playerFilter.cloudUserId || playerFilter.sessionId);
    const heading = playerLabel
        || (playerFilter.cloudUserId ? "Signed-in player" : playerFilter.sessionId ? "Guest session" : "");

    return (
        <Box sx={{ flexGrow: 1, height: "50%", display: "flex", flexDirection: "column", gap: 2 }}>
            {hasPlayerFilter ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Play list
                    </Typography>
                    <Chip
                        label={heading}
                        color={playerFilter.cloudUserId ? "primary" : "default"}
                        variant={playerFilter.cloudUserId ? "filled" : "outlined"}
                    />
                    <Link
                        component={RouterLink}
                        to="/admin/puzzle/word_wheel_stats?tab=1"
                        underline="hover"
                        sx={{ fontWeight: 600 }}
                    >
                        Back to stats
                    </Link>
                    <Link
                        component={RouterLink}
                        to="/admin/puzzle/word_wheel_play"
                        underline="hover"
                    >
                        All plays
                    </Link>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={viewMode}
                        onChange={(_, next) => {
                            if (next) setViewMode(next);
                        }}
                        aria-label="Play view"
                    >
                        <ToggleButton value="list" aria-label="List">
                            List
                        </ToggleButton>
                        <ToggleButton value="chart" aria-label="Chart">
                            Chart
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            ) : null}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <KeywordBox callback={setKeyword} />
                    <FormGroup row>
                        <FormControlLabel control={<Checkbox checked={completionStatus.all} onChange={() => handleCompletionStatusChange("all")} color="primary" />} label="All" />
                        <FormControlLabel control={<Checkbox checked={completionStatus.completed} onChange={() => handleCompletionStatusChange("completed")} color="primary" />} label="Completed" />
                        <FormControlLabel control={<Checkbox checked={completionStatus.incomplete} onChange={() => handleCompletionStatusChange("incomplete")} color="primary" />} label="Incomplete" />
                        <FormControlLabel control={<Checkbox checked={onlyStarted} onChange={(e) => setOnlyStarted(e.target.checked)} color="primary" />} label="Only Started" />
                    </FormGroup>
                </Box>
                <Tooltip title={`Delete ${selectedRowIds.length} selected row${selectedRowIds.length !== 1 ? "s" : ""}`}>
                    <span>
                        <IconButton color="error" onClick={handleDeleteClick} disabled={selectedRowIds.length === 0}>
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            {viewMode === "chart" && hasPlayerFilter ? (
                chartLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : chartError ? (
                    <Typography color="error">{chartError}</Typography>
                ) : (
                    <WordWheelPlayTimelineChart rows={chartRows} />
                )
            ) : (
                <DataGrid
                    rows={rows}
                    columns={getSolveColumns()}
                    rowCount={rowCount}
                    pageSizeOptions={[15, 25, 30]}
                    paginationMode="server"
                    sortingMode="server"
                    pagination
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    checkboxSelection
                    onCellClick={handleCell}
                    onRowDoubleClick={handleRowDoubleClick}
                    onSortModelChange={handleSort}
                    onRowSelectionModelChange={handleSelectionModel}
                    columnVisibilityModel={{ id: false }}
                    hideFooterSelectedRowCount
                />
            )}
            <PlayRowDetailDialog
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                rowData={selectedRowForDetail}
                puzzleKey="grid"
            />
            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Confirm Delete"
                message={`Delete ${selectedRowIds.length} play record(s)?`}
                warningAlert="Testing only — not for production user history."
                targetId={selectedRowIds}
                callback={handleDeleteConfirm}
            />
        </Box>
    );
}
